/**
 * Diagrams in Markdown: a ```mermaid fence draws instead of showing code, in every place the
 * Markdown component renders (item descriptions, the requirements document, mission goals,
 * comments, proposals). The pure parts live here so they can be tested without a browser:
 * which fences are diagrams, what kind of diagram a source is, the theme built from the tokens
 * so a diagram follows the active theme, and the scrub that keeps a drawn diagram from reaching
 * out of the page. The drawing itself is in components/Diagram.tsx, where mermaid loads on
 * first use as its own chunk.
 */

/** Fence languages drawn as a diagram. */
export function isDiagramLang(lang: string): boolean {
  return lang.trim().toLowerCase() === 'mermaid';
}

const KINDS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^(flowchart|graph)\b/i, 'Flowchart'],
  [/^(sequenceDiagram|zenuml)\b/i, 'Sequence diagram'],
  [/^classDiagram/i, 'Class diagram'],
  [/^stateDiagram/i, 'State diagram'],
  [/^erDiagram/i, 'Entity relationship diagram'],
  [/^gantt\b/i, 'Gantt chart'],
  [/^pie\b/i, 'Pie chart'],
  [/^journey\b/i, 'User journey'],
  [/^gitGraph\b/i, 'Git graph'],
  [/^mindmap\b/i, 'Mind map'],
  [/^timeline\b/i, 'Timeline'],
  [/^quadrantChart\b/i, 'Quadrant chart'],
  [/^requirementDiagram\b/i, 'Requirement diagram'],
  [/^C4(Context|Container|Component|Dynamic|Deployment)\b/, 'C4 diagram'],
  [/^sankey(-beta)?\b/i, 'Sankey diagram'],
  [/^xychart(-beta)?\b/i, 'XY chart'],
  [/^block(-beta)?\b/i, 'Block diagram'],
  [/^packet(-beta)?\b/i, 'Packet diagram'],
  [/^kanban\b/i, 'Kanban board'],
  [/^architecture(-beta)?\b/i, 'Architecture diagram'],
  [/^radar(-beta)?\b/i, 'Radar chart'],
  [/^treemap(-beta)?\b/i, 'Treemap'],
];

/** The first line of a diagram source that is not a directive, a comment or front matter. */
export function diagramHead(source: string): string {
  let s = source.replace(/^﻿/, '').trim();
  // Front matter (--- ... ---) and init directives (%%{ ... }%%) come before the kind.
  if (s.startsWith('---')) {
    const end = s.indexOf('\n---', 3);
    s = end < 0 ? '' : s.slice(end + 4).trim();
  }
  for (;;) {
    const m = /^%%\{[\s\S]*?\}%%\s*/.exec(s);
    if (!m) break;
    s = s.slice(m[0].length);
  }
  for (const line of s.split('\n')) {
    const t = line.trim();
    if (t && !t.startsWith('%%')) return t;
  }
  return '';
}

/** What kind of diagram a Mermaid source draws, for the frame's label. */
export function diagramKind(source: string): string {
  const head = diagramHead(source);
  for (const [re, label] of KINDS) if (re.test(head)) return label;
  return 'Diagram';
}

/** The theme a diagram is drawn with: mermaid's base theme fed from the tokens. */
export interface DiagramTheme {
  /** Light text on a dark panel, or the reverse: mermaid derives the colours it is not given from this. */
  darkMode: boolean;
  fontFamily: string;
  fontSize: string;
  variables: Record<string, string>;
}

/** Relative luminance of a #rrggbb colour (0 black to 1 white); 0.5 when it is not one. */
export function hexLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0.5;
  const n = parseInt(m[1]!, 16);
  const ch = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(n >> 16) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
}

/**
 * Build the theme from a token reader (`read('fg')` is the value of `--fg`). Nodes sit on the
 * accent's soft fill with its line; text is the foreground; lines and arrows the muted
 * foreground; groups, notes and loops the raised surface; series (pie slices, git branches,
 * mind map branches, chart lines) take the chart palette slots in order; nothing takes a
 * status colour.
 */
export function diagramTheme(read: (token: string) => string): DiagramTheme {
  const v = (t: string) => read(t).trim();
  const fg = v('fg');
  const muted = v('fg-muted');
  const subtle = v('fg-subtle');
  const surface = v('surface');
  const raised = v('surface-raised');
  const hover = v('surface-hover');
  const border = v('border');
  const strong = v('border-strong');
  const accent = v('accent');
  const accentSoft = v('accent-soft');
  const accentLine = v('accent-line');
  const accentFg = v('accent-fg');
  const data = Array.from({ length: 8 }, (_, i) => v(`data-${i + 1}`));
  const variables: Record<string, string> = {
    darkMode: String(hexLuminance(surface) < 0.5),
    background: surface,
    fontFamily: v('font-sans'),
    fontSize: v('font-size-caption'),
    // Nodes, text and lines.
    mainBkg: accentSoft,
    primaryColor: accentSoft,
    primaryTextColor: fg,
    primaryBorderColor: accentLine,
    secondaryColor: hover,
    secondaryTextColor: fg,
    secondaryBorderColor: border,
    tertiaryColor: raised,
    tertiaryTextColor: muted,
    tertiaryBorderColor: border,
    lineColor: muted,
    textColor: fg,
    titleColor: fg,
    nodeBorder: accentLine,
    nodeTextColor: fg,
    clusterBkg: raised,
    clusterBorder: border,
    edgeLabelBackground: surface,
    labelBackgroundColor: surface,
    noteBkgColor: raised,
    noteTextColor: fg,
    noteBorderColor: border,
    // Sequence diagrams.
    actorBkg: accentSoft,
    actorBorder: accentLine,
    actorTextColor: fg,
    actorLineColor: strong,
    signalColor: muted,
    signalTextColor: fg,
    labelBoxBkgColor: raised,
    labelBoxBorderColor: border,
    labelTextColor: fg,
    loopTextColor: muted,
    activationBkgColor: hover,
    activationBorderColor: strong,
    sequenceNumberColor: accentFg,
    // State and class diagrams.
    transitionColor: muted,
    transitionLabelColor: fg,
    stateLabelColor: fg,
    stateBkg: accentSoft,
    compositeBackground: raised,
    compositeTitleBackground: raised,
    compositeBorder: border,
    altBackground: hover,
    innerEndBackground: strong,
    specialStateColor: muted,
    classText: fg,
    // Entity relationship diagrams.
    attributeBackgroundColorOdd: surface,
    attributeBackgroundColorEven: hover,
    // Gantt charts: the accent for work, the raised surface for sections, no red for critical.
    sectionBkgColor: raised,
    sectionBkgColor2: hover,
    altSectionBkgColor: surface,
    excludeBkgColor: surface,
    gridColor: border,
    todayLineColor: strong,
    taskBkgColor: accentSoft,
    taskBorderColor: accentLine,
    taskTextColor: fg,
    taskTextLightColor: fg,
    taskTextDarkColor: fg,
    taskTextOutsideColor: fg,
    taskTextClickableColor: fg,
    activeTaskBkgColor: accent,
    activeTaskBorderColor: accent,
    doneTaskBkgColor: hover,
    doneTaskBorderColor: strong,
    critBkgColor: accentSoft,
    critBorderColor: strong,
    // Pie charts and git graphs: the chart palette in slot order.
    pieTitleTextColor: fg,
    pieSectionTextColor: fg,
    pieLegendTextColor: fg,
    pieStrokeColor: surface,
    pieOuterStrokeColor: border,
    commitLabelColor: fg,
    commitLabelBackground: surface,
    tagLabelColor: fg,
    tagLabelBackground: raised,
    tagLabelBorder: border,
    // Errors mermaid draws itself are never shown (suppressErrorRendering), but keep them quiet.
    errorBkgColor: raised,
    errorTextColor: subtle,
  };
  data.forEach((c, i) => {
    variables[`pie${i + 1}`] = c;
    variables[`git${i}`] = c;
    variables[`cScale${i}`] = c;
    variables[`cScaleLabel${i}`] = fg;
  });
  // Mind maps and timelines use 12 scale slots: slots 9 to 12 cycle the palette.
  for (let i = 8; i < 12; i++) {
    variables[`cScale${i}`] = data[i - 8]!;
    variables[`cScaleLabel${i}`] = fg;
  }
  return { darkMode: variables.darkMode === 'true', fontFamily: variables.fontFamily!, fontSize: variables.fontSize!, variables };
}

/**
 * CSS that mermaid puts inside the drawn SVG, with anything that reaches out of the page
 * removed: @import rules and url() values (a source can add CSS through an init directive).
 */
export function scrubDiagramCss(css: string): string {
  return css.replace(/@import[^;]*;?/gi, '').replace(/url\s*\([^)]*\)/gi, 'none');
}

/** Whether a link target inside a drawn diagram may stay: only fragments and data images. */
export function safeDiagramHref(href: string | null): boolean {
  if (href == null) return true;
  const h = href.trim();
  return h === '' || h.startsWith('#') || /^data:image\//i.test(h);
}
